import React, { useRef, useEffect, useState } from 'react';
import { useWallet } from '../context/WalletContext';
import '../styles/switch-network-modal.css';

const SwitchNetworkModal = ({ isOpen, onClose, currentNetwork, onSwitch }) => {
  const { wallet } = useWallet();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const modalRef = useRef(null);
  
  // Common networks to show in the modal
  const networks = [
    { 
      id: 'ethereum', 
      name: 'Ethereum', 
      color: '#627EEA',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2L11.9671 2.11173V16.3215L12 16.3544L18.5856 12.5639L12 2Z" fill="#627EEA"/>
          <path d="M12 2L5.41443 12.5639L12 16.3544V9.66548V2Z" fill="#C0CBF6"/>
          <path d="M12 17.4733L11.9806 17.497V22L12 22.0001L18.5882 13.684L12 17.4733Z" fill="#627EEA"/>
          <path d="M12 22.0001V17.4733L5.41443 13.684L12 22.0001Z" fill="#C0CBF6"/>
          <path d="M12 16.3543L18.5856 12.5639L12 9.66547V16.3543Z" fill="#3C4E9A"/>
          <path d="M5.41443 12.5639L12 16.3543V9.66547L5.41443 12.5639Z" fill="#6979BF"/>
        </svg>
      )
    },
    { 
      id: 'bsc', 
      name: 'BNB Smart Chain', 
      color: '#F0B90B',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2L14.25 4.25L7.75 10.75L5.5 8.5L12 2Z" fill="#F0B90B"/>
          <path d="M16.5 6.5L14.25 4.25L20.75 10.75L18.5 13L12 19.5L5.5 13L7.75 10.75L12 15L16.25 10.75L7.75 10.75L12 6.5L16.5 6.5Z" fill="#F0B90B"/>
          <path d="M18.5 15.5L16.25 13.25L18.5 11L20.75 13.25L18.5 15.5Z" fill="#F0B90B"/>
        </svg>
      )
    },
    { 
      id: 'solana', 
      name: 'Solana', 
      color: '#9945FF',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M5.8147 16.2009C5.91278 16.1028 6.04458 16.0499 6.18248 16.0499H20.9204C21.1587 16.0499 21.2778 16.3371 21.1089 16.506L18.1853 19.4299C18.0872 19.528 17.9554 19.5809 17.8175 19.5809H3.0796C2.84133 19.5809 2.72221 19.2938 2.89113 19.1249L5.8147 16.2009Z" fill="#9945FF"/>
          <path d="M5.8147 4.57007C5.91874 4.47195 6.05055 4.41907 6.18248 4.41907H20.9204C21.1587 4.41907 21.2778 4.70621 21.1089 4.87512L18.1853 7.79905C18.0872 7.89718 17.9554 7.95005 17.8175 7.95005H3.0796C2.84133 7.95005 2.72221 7.66292 2.89113 7.49401L5.8147 4.57007Z" fill="#9945FF"/>
          <path d="M18.1853 10.3663C18.0872 10.2682 17.9554 10.2153 17.8175 10.2153H3.0796C2.84133 10.2153 2.72221 10.5024 2.89113 10.6714L5.8147 13.5953C5.91278 13.6934 6.04458 13.7463 6.18248 13.7463H20.9204C21.1587 13.7463 21.2778 13.4591 21.1089 13.2902L18.1853 10.3663Z" fill="#9945FF"/>
        </svg>
      )
    },
  ];
  
  // Handle clicks outside the modal to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target) && !isProcessing) {
        onClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = '';
    };
  }, [isOpen, isProcessing, onClose]);
  
  // Reset error state when modal opens
  useEffect(() => {
    if (isOpen) {
      setError(null);
    }
  }, [isOpen]);
  
  // Helper function to check if wallet type supports Solana
  const isSolanaCompatible = (walletType) => {
    return ['phantom'].includes(walletType?.toLowerCase());
  };
  
  // Handle network switch
  const handleNetworkSwitch = async (networkId) => {
    if (networkId === currentNetwork) {
      onClose();
      return;
    }
    
    setIsProcessing(true);
    setError(null);
    
    try {
      // Check if current wallet supports the selected network
      // This checks before even attempting to switch and provides better UX
      if (networkId === 'solana' && !isSolanaCompatible(wallet.type)) {
        throw new Error('Current wallet doesn\'t support Solana. Please connect a Solana-compatible wallet');
      }
      
      if ((networkId === 'ethereum' || networkId === 'bsc') && wallet.type?.toLowerCase() === 'phantom') {
        throw new Error('Phantom wallet only supports Solana. Please connect an Ethereum-compatible wallet');
      }
      
      const success = await onSwitch(networkId);
      if (success) {
        setTimeout(() => {
          onClose();
        }, 500);
      }
    } catch (err) {
      console.error('Network switch error:', err);
      setError(err.message || 'Failed to switch network');
    } finally {
      setIsProcessing(false);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="network-modal-overlay" aria-modal="true" role="dialog">
      <div className="network-modal" ref={modalRef}>
        <button 
          className="network-modal-close" 
          onClick={onClose} 
          aria-label="Close"
          disabled={isProcessing}
        >
          ×
        </button>
        
        <div className="network-modal-header">
          <h2>Switch Network</h2>
          <p>Select the blockchain network you want to use</p>
        </div>
        
        <div className="network-options">
          {networks.map((network) => (
            <button 
              key={network.id}
              className={`network-option ${currentNetwork === network.id ? 'active' : ''} ${isProcessing ? 'disabled' : ''}`}
              onClick={() => handleNetworkSwitch(network.id)}
              disabled={isProcessing}
              style={{ 
                '--network-color': network.color 
              }}
            >
              <div className="network-icon">
                {network.icon}
              </div>
              <div className="network-info">
                <h3>{network.name}</h3>
                {currentNetwork === network.id && (
                  <span className="current-network-badge">Current</span>
                )}
              </div>
              {isProcessing && currentNetwork !== network.id && (
                <div className="network-processing">
                  <span className="spinner"></span>
                </div>
              )}
            </button>
          ))}
        </div>
        
        {error && (
          <div className="network-error" role="alert">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" stroke="#ff5252" strokeWidth="2"/>
              <path d="M12 8V12" stroke="#ff5252" strokeWidth="2" strokeLinecap="round"/>
              <path d="M12 16H12.01" stroke="#ff5252" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <p>{error}</p>
          </div>
        )}
        
        <div className="network-modal-footer">
          <p>Some wallets may not support all networks. If switching fails, try connecting a different wallet.</p>
        </div>
      </div>
    </div>
  );
};

export default SwitchNetworkModal;
